import { Group, MeshStandardMaterial, Object3D } from 'three';

import { BaseKnobs } from './BaseKnobs.js';
import { Labels } from './Labels.js';
import { RangeParts } from '../parts/RangeParts.js';
import { RangeTopOptions } from '../shared/RangeTopOptions.js';

import { AssetLoader } from '../../shared/AssetLoader.js';
import {
  Burner,
  LabelName,
  Line,
  MeshName,
  RangeModel,
  OptionSpace,
  RangeTop,
  Region,
  Spacer,
} from '../../shared/Enums.js';
import { Materials } from '../../shared/Materials.js';
import { RangeState } from '../../shared/RangeState.js';

export class BaseRange {
  /** All range meshes should be added to (or removed from) this group */
  range = new Group();

  /**
   * The 3D models for the range
   * @type {AssetLoader}
   */
  assets;

  /**
   * Applies various materials to various parts of the range
   * @type {Materials}
   */
  materials;

  /**
   * Parts of the range that are common to all models
   * @type {RangeParts}
   */
  rangeParts;

  /**
   * The optional burners including the flame grill, plancha, multi-cooker, and
   * traditional simmer plate
   * @type {RangeTopOptions}
   */
  rangeTopOptions;

  /**
   * The classique trim for the selected range
   * @type {Group}
   */
  classiqueTrim;

  /**
   * The moderne trim for the selected range
   * @type {Group}
   */
  moderneTrim;

  /** The core 3 or 4 burners in the middle of the range */
  baseBurners = new Group();

  /**
   * Controls the placement of the knobs on the Control Panel
   * @type {BaseKnobs}
   */
  knobs;

  /** The current configuration of the range */
  state;

  /**
   * The descriptive labels for various parts of the range
   * @type {Labels}
   */
  labels;

  /**
   * @typedef Features
   * @type {object}
   * @property {boolean} farLeftOption - Show a selection of far left (over cupboard) burner options
   * @property {boolean} leftOption - Show a selection of left burner options
   * @property {boolean} rightOption - Show a selection of left burner options
   * @property {boolean} farRightOption - Show a selection of far right (over cupboard) burner options
   * @property {boolean} rightOven - Show a selection of right oven options
   * @property {boolean} convection - Allow the convection oven option
   * @property {boolean} noGasOven - Don't allow the left gas oven option
   * @property {boolean} leftCupboard - Show a selection of left cupboard options
   * @property {boolean} rightCupboard - Show a selection of right cupboard options
   * @property {boolean} traditional - Enable the traditional option for range tops
   * @property {boolean} fourFeux - Enable the 4-feux option for range tops
   * @property {boolean} classiqueRev - Enable the classique reverse option for range tops
   */

  /**
   * The UI features that are available
   * @type {Features}
   */
  features = {
    farLeftOption: false,
    leftOption: false,
    rightOption: false,
    rightOven: false,
    farRightOption: false,
    convection: false,
    noGasOven: false,
    leftCupboard: false,
    rightCupboard: false,
    traditional: true,
    fourFeux: false,
    classiqueRev: false,
  };

  /**
   * @param {AssetLoader} assets
   * @param {Materials} materials
   * @param {RangeParts} rangeParts
   * @param {RangeTopOptions} rangeTopOptions
   * @param {RangeState} state
   */
  constructor(assets, materials, rangeParts, rangeTopOptions, state) {
    this.assets = assets;
    this.materials = materials;
    this.rangeParts = rangeParts;
    this.rangeTopOptions = rangeTopOptions;
    this.state = state;
    this.labels = new Labels(state);

    this.state.petiteOven = null;
    this.state.spacer = Spacer.flush;

    this.#resetRangeTop();
  }

  /**
   * Change the line of the range to classique or moderne
   * @param {string} line
   */
  changeLine(line) {
    this.changeControlPanelAndSide(line);
    this.knobs.changeLine();

    if (line === Line.moderne) {
      this.range.remove(this.classiqueTrim);
      this.range.add(this.moderneTrim);
    } else if (line === Line.classique) {
      this.range.remove(this.moderneTrim);
      this.range.add(this.classiqueTrim);
    }
  }

  /**
   * Change the trim of the range to brass, brushed stainless steel, chrome, or nickel
   * @param {string} trim
   */
  changeTrim(trim) {
    this.materials.changeTrim(trim, this.trimParts());
    this.materials.changeTrim(trim, this.#flameGrillTrim());
    this.knobs.changeTrim(trim);
    this.knobs.changeLEDBezels(trim);
  }

  /**
   * Change the color of the porcelain enamel panels on the range
   * @param {string} color
   */
  changeColor(color) {
    this.materials.changeColor(color, this.colorParts());
    this.knobs.changeKnobDialColor();
    this.labels.changeColor();

    if (this.state.line !== Line.moderne) {
      this.materials.changeColor(color, this.#controlPanelAndSides());
    }
  }

  /**
   * Change the left oven to gas, electric, or electric convection
   * @param {string} ovenType
   */
  changeLeftOven(ovenType) {
    this.knobs.changeLeftOven(ovenType);
    this.labels.changeText(LabelName.leftOven, ovenType);
  }

  /**
   * Change the right oven to gas, electric, or electric convection
   * @param {string} ovenType
   */
  changeRightOven(ovenType) {
    this.knobs.changeRightOven(ovenType);
    this.labels.changeText(LabelName.rightOven, ovenType);
  }

  /**
   * Change the elevation of the back spacer to raised or flush
   * @param {string} spacerElevation
   */
  changeSpacer(spacerElevation) {
    if (this.state.spacer === spacerElevation) {
      return;
    }

    const backSpacer = this.range.getObjectByName(MeshName.backSpacer);
    const backSpacerBlack = this.range.getObjectByName(
      MeshName.backSpacerBlack
    );
    const elevation = spacerElevation === Spacer.raised ? 0.0505 : -0.0505;

    backSpacer.translateY(elevation);
    backSpacerBlack.translateY(elevation);
  }

  /**
   * Show or hide the labels for the various range parts
   * @param {boolean} showLabels
   */
  displayLabels(showLabels) {
    if (showLabels) {
      this.labels.front.visible = true;
      this.labels.top.visible = true;
    } else {
      this.labels.front.visible = false;
      this.labels.top.visible = false;
    }
  }

  /**
   * Only show the front labels.
   */
  displayFrontLabels() {
    this.labels.front.visible = true;
    this.labels.top.visible = false;
  }

  /**
   * Only show the front labels.
   */
  displayTopLabels() {
    this.labels.front.visible = false;
    this.labels.top.visible = true;
  }

  /**
   * Change the material of the porcelain enamel panels on the range.
   * This is used for manually choosing a material from the GUI
   * @param {MeshStandardMaterial} material
   */
  changeMaterial(material) {
    this.materials.changeMaterial(material, this.colorParts());

    if (this.state.line !== Line.moderne) {
      this.materials.changeMaterial(material, this.#controlPanelAndSides());
    }

    return material;
  }

  /**
   * Change the first optional "burner" of the range top
   * @param {string} burnerOption
   * @param {boolean} [assembleTop]
   */
  changeOption1(burnerOption, assembleTop = true) {
    this.changeBurner(burnerOption, OptionSpace.option1, assembleTop);
  }

  /**
   * Change the optional "burner" in the left position of the range top
   * @param {string} burnerOption
   * @param {boolean} [assembleTop]
   */
  changeOption2(burnerOption, assembleTop = true) {
    this.changeBurner(burnerOption, OptionSpace.option2, assembleTop);
  }

  /**
   * Change the optional "burner" in the right position of the range top
   * @param {string} burnerOption
   * @param {boolean} [assembleTop]
   */
  changeOption3(burnerOption, assembleTop = true) {
    this.changeBurner(burnerOption, OptionSpace.option3, assembleTop);
  }

  /**
   * Change the optional "burner" in the far right position of the range top
   * @param {string} burnerOption
   * @param {boolean} [assembleTop]
   */
  changeOption4(burnerOption, assembleTop = true) {
    this.changeBurner(burnerOption, OptionSpace.option4, assembleTop);
  }

  /**
   * Add a two burner (11k front, 5k rear) option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add11And5KBurners(xOffset, group) {
    const elevenAnd5KBurners = this.rangeTopOptions.elevenAnd5KBurners.clone();
    elevenAnd5KBurners.translateX(xOffset);

    group.add(elevenAnd5KBurners);
  }

  /**
   * Add a two burner (5k front, 11k rear) option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add5And11KBurners(xOffset, group) {
    const fiveAnd11KBurners = this.rangeTopOptions.fiveAnd11KBurners.clone();
    fiveAnd11KBurners.translateX(xOffset);

    group.add(fiveAnd11KBurners);
  }

  /**
   * Add a two burner (15k front, 5k rear) option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add15And5KBurners(xOffset, group) {
    const fifteenAnd5KBurners =
      this.rangeTopOptions.fifteenAnd5KBurners.clone();
    fifteenAnd5KBurners.translateX(xOffset);

    group.add(fifteenAnd5KBurners);
  }

  /**
   * Add a two burner (15k front, 11k rear) option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add15And11KBurners(xOffset, group) {
    const fifteenAnd11KBurners =
      this.rangeTopOptions.fifteenAnd11KBurners.clone();
    fifteenAnd11KBurners.translateX(xOffset);

    group.add(fifteenAnd11KBurners);
  }

  /**
   * Add a two 11k burners option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add11KBurners(xOffset, group) {
    const elevenKBurners = this.rangeTopOptions.elevenKBurners.clone();
    elevenKBurners.translateX(xOffset);

    group.add(elevenKBurners);
  }

  /**
   * Add a two 15k burners option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add15KBurners(xOffset, group) {
    const fifteenKBurners = this.rangeTopOptions.fifteenKBurners.clone();
    fifteenKBurners.translateX(xOffset);

    group.add(fifteenKBurners);
  }

  /**
   * Add an 18k (classique) burner option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  add18KBurner(xOffset, group) {
    const eighteenKBurner = this.rangeTopOptions.eighteenKBurner.clone();
    eighteenKBurner.translateX(xOffset);

    group.add(eighteenKBurner);
  }

  /**
   * Add two spacer tabs for the left side of a two burner grate
   * @param {number} xOffset
   * @param {Group} group
   */
  addGrateLeftSpacerTabs(xOffset, group) {
    const spacer = this.rangeTopOptions.leftGrateSpacerTabs.clone();
    spacer.translateX(xOffset);

    group.add(spacer);
  }

  /**
   * Add two spacers for the right side of a two burner grate
   * @param {number} xOffset
   * @param {Group} group
   */
  addGrateRightSpacerTabs(xOffset, group) {
    const spacer = this.rangeTopOptions.rightGrateSpacerTabs.clone();
    spacer.translateX(xOffset);

    group.add(spacer);
  }

  /**
   * Add intermediate spacer grate used in most 4-feux configurations
   * @param {number} xOffset
   * @param {Group} group
   */
  async add4FeuxSpacerGrate(xOffset, group) {
    const spacer = await this.rangeTopOptions.fourFeuxSpacerGrate;
    const spacerClone = spacer.clone();
    spacerClone.position.x += xOffset;

    group.add(spacerClone);
  }

  /**
   * Add side spacer tabs to separate two burner grates used in a Sully 4-feux
   * @param {number} xOffset
   * @param {Group} group
   */
  addSully4FeuxGrateSpacerTabs(xOffset, group) {
    const spacer = this.rangeTopOptions.fourFeuxSpacerTabs.clone();
    spacer.translateX(xOffset);

    group.add(spacer);
  }

  /**
   * Add a traditional simmer plate to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  async addTradPlate(xOffset, group) {
    const tradPlateGroup = await this.rangeTopOptions.tradPlateGroup;
    const tradPlateClone = tradPlateGroup.clone();
    tradPlateClone.translateX(xOffset);

    group.add(tradPlateClone);
  }

  /**
   * Add a multi-cooker to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  async addMultiCooker(xOffset, group) {
    const multiCooker = await this.rangeTopOptions.multiCooker;
    const multiCookerClone = multiCooker.clone();
    multiCookerClone.translateX(xOffset);

    group.add(multiCookerClone);
  }

  /**
   * Add an electric plancha to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  async addPlancha(xOffset, group) {
    const plancha = await this.rangeTopOptions.plancha;
    const clonedPlancha = plancha.clone();
    clonedPlancha.translateX(xOffset);

    group.add(clonedPlancha);
  }

  /**
   * Add a flame grill to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  async addFlameGrill(xOffset, group) {
    const flameGrill = await this.rangeTopOptions.flameGrill;
    const flameGrillClone = flameGrill.clone();
    flameGrillClone.translateX(xOffset);
    this.materials.changeTrim(this.state.trim, [
      flameGrillClone.getObjectByName('RO2_Handel_GG'),
      flameGrillClone.getObjectByName('RO2_Handel_GG001'),
    ]);

    group.add(flameGrillClone);
  }

  /**
   * Add a two induction rings option to the range top
   * @param {number} xOffset
   * @param {Group} group
   */
  async addInductionRings(xOffset, group) {
    const inductionRings = await this.rangeTopOptions.inductionRings;
    const inductionRingsClone = inductionRings.clone();
    inductionRingsClone.translateX(xOffset);

    group.add(inductionRingsClone);
  }

  /**
   * For moderne, change the control panel and sides to stainless steel
   * @param {string} line
   */
  changeControlPanelAndSide(line) {
    if (line === Line.moderne) {
      this.materials.applyStainlessSteelMaterial(
        this.range.getObjectByName(MeshName.controlPanel),
        this.range.getObjectByName(MeshName.rangeSides)
      );
    } else if (line === Line.classique) {
      this.materials.changeColor(this.state.color, [
        this.range.getObjectByName(MeshName.controlPanel),
        this.range.getObjectByName(MeshName.rangeSides),
      ]);
    }
  }

  /** Apply the stainless steel and galvanized steel materials to the corresponding meshes */
  applyMaterials() {
    this.materials.applyStainlessSteelMaterial(...this.stainlessSteelParts());
    this.materials.applySSRangeTopMaterial(...this.ssRangeTopParts());

    this.materials.applyGalvanizedSteelMaterial(...this.galvanizedSteelParts());
  }

  showCorrectOvenVent(burnerOption, region) {
    const showMiniVent = burnerOption === Burner.electricPlancha;

    switch (region) {
      case Region.left:
        this.range.getObjectByName(MeshName.leftOvenVentCover).visible =
          !showMiniVent;
        this.range.getObjectByName(MeshName.leftOvenMiniVentCover).visible =
          showMiniVent;
        break;

      case Region.right:
        this.range.getObjectByName(MeshName.rightOvenVentCover).visible =
          !showMiniVent;
        this.range.getObjectByName(MeshName.rightOvenMiniVentCover).visible =
          showMiniVent;
        break;
    }
  }

  /**
   * Configure meshes to cast and receive shadows
   */
  setupShadows() {
    this.shadowCasters().forEach((mesh) => {
      mesh.castShadow = true;
    });

    this.shadowReceivers().forEach((mesh) => {
      mesh.receiveShadow = true;
    });
  }

  addRangeLabels() {
    this.labels.addRangeFrontLabels();
  }

  /**
   * Finish initializing the range
   * @abstract
   */
  async init() {
    // This should be implemented by subclasses that need to load 3D model asynchronously
  }

  /**
   * Change the range top style to classique, traditional, or 4-feux
   * @abstract
   * @param {string} rangeTop
   * @param {boolean} [assembleTop] Whether to assemble a new top and add it to the range
   */
  changeRangeTop(rangeTop, assembleTop = true) {
    throw new Error('This method must be implemented by subclass!');
  }

  /**
   * Change the one of the optional "burners" on the range top
   * @abstract
   * @param {string} burnerOption
   * @param {string} optionSpace
   * @param {boolean} [assembleTop]
   */
  changeBurner(burnerOption, optionSpace, assembleTop) {
    throw new Error('This method must be implemented by subclass!');
  }

  /**
   * Change the left cupboard to warming or storage
   * @abstract
   * @param {string} type
   */
  changeLeftCupboard(type) {
    throw new Error('This method must be implemented by subclass!');
  }

  /**
   * Change the right cupboard to warming or storage
   * @abstract
   * @param {string} type
   */
  changeRightCupboard(type) {
    throw new Error('This method must be implemented by subclass!');
  }

  /**
   * All the trim parts of the range that can be brass, brushed stainless steel, chrome, or nickel
   * @abstract
   * @returns {Object3D[]}
   */
  trimParts() {
    return [];
  }

  /**
   * All the parts of the range that are porcelain enamel
   * @abstract
   * @returns {Object3D[]}
   */
  colorParts() {
    return [];
  }

  /**
   * All the parts of the range that are stainless steel
   * @abstract
   * @returns {Object3D[]}
   */
  stainlessSteelParts() {
    return [];
  }

  /**
   * The stainless steel range top
   * @abstract
   * @returns {Object3D[]}
   */
  ssRangeTopParts() {
    return [];
  }

  /**
   * All the parts of the range that are galvanized steel
   * @abstract
   * @returns {Object3D[]}
   */
  galvanizedSteelParts() {
    return [];
  }

  /**
   * All the parts of the range that receive shadows
   * @abstract
   * @returns {Object3D[]}
   */
  shadowCasters() {
    return [];
  }

  /**
   * All the parts of the range that receive shadows
   * @abstract
   * @returns {Object3D[]}
   */
  shadowReceivers() {
    return [];
  }

  /**
   * The trim parts for the flame grill (if applicable)
   * @returns {Object3D[]}
   */
  #flameGrillTrim() {
    if (!this.range.getObjectByName('RO2_Handel_GG')) {
      return [];
    }

    return [
      this.range.getObjectByName('RO2_Handel_GG'), // flame grill rear posts
      this.range.getObjectByName('RO2_Handel_GG001'), // flame grill handles
    ];
  }

  /**
   * Reset the range top to a valid configuration
   */
  #resetRangeTop() {
    if (
      this.#nonClassiqueRevRange() &&
      this.state.top === RangeTop.classiqueRev
    ) {
      this.state.top = RangeTop.classique;
    }

    if (this.#non4FeuxRange() && this.state.top === RangeTop.fourFeux) {
      this.state.top = RangeTop.classique;
    }
  }

  /**
   * Is the range model not compatible with a classique reverse range top?
   * @returns {boolean}
   */
  #nonClassiqueRevRange() {
    return (
      this.state.model !== RangeModel.beaune &&
      this.state.model !== RangeModel.bussy
    );
  }

  /**
   * Is the range model not compatible with a 4-feux range top?
   * @returns {boolean}
   */
  #non4FeuxRange() {
    const nonFourFeuxModels = [
      RangeModel.beaune,
      RangeModel.bussy,
      RangeModel.chagny,
      RangeModel.chagny1400left,
      RangeModel.chagny1400right,
      RangeModel.chagny1800,
      RangeModel.cluny,
      RangeModel.cluny1400left,
      RangeModel.cluny1400right,
      RangeModel.cluny1800,
      RangeModel.volnay,
      RangeModel.vougeot,
    ];

    return nonFourFeuxModels.includes(this.state.model);
  }

  /**
   * The meshes for a control panel and the sides of the range
   * @returns {Object3D[]}
   */
  #controlPanelAndSides() {
    return [
      this.range.getObjectByName(MeshName.controlPanel),
      this.range.getObjectByName(MeshName.rangeSides),
    ];
  }
}
